export type OrgAuthClaimStage = "preview" | "submit";

export interface OrgAuthClaimErrorBody {
    error?: string;
    code?: string;
}

export interface OrgAuthClaimToken {
    tokenUuid: string;
    secret: string;
}

export type OrgAuthClaimErrorScope = "field" | "form" | "page";

export interface OrgAuthClaimErrorResolution {
    message: string;
    scope: OrgAuthClaimErrorScope;
    retryable: boolean;
}

export const ORG_AUTH_CLAIM_NETWORK_ERROR = "Unable to reach the server. Check your connection and try again.";

export const ORG_AUTH_CLAIM_PASSWORD_LENGTH_ERROR = "Password must be between 8 and 72 characters.";

const CLAIM_TOKEN_ERROR_MESSAGES: Record<string, string> = {
    TOKEN_NOT_FOUND:
        "This link is not valid. Open the latest invite or password reset link, or ask the admin who sent it for a new one.",
    TOKEN_INVALID:
        "This link is not valid. Open the latest invite or password reset link, or ask the admin who sent it for a new one.",
    TOKEN_EXPIRED: "This link has expired. Ask the admin who sent it for a new one.",
    TOKEN_CONSUMED:
        "This link has already been used. Try signing in, or ask for a new reset link if you still cannot access the console.",
    TOKEN_REVOKED: "This link has been revoked. Ask the admin who sent it for a new one.",
    USER_SUSPENDED: "Your account is suspended, so this link cannot be used. Contact your admin.",
    USER_NOT_FOUND:
        "This link is not valid. Open the latest invite or password reset link, or ask the admin who sent it for a new one.",
    ORG_INACTIVE: "This organization is inactive, so this console account cannot be activated. Contact support."
};

const TECHNICAL_CLAIM_MESSAGE =
    /^(internal server error|bad request|invalid claim token|too many requests|request failed|failed to fetch|networkerror|unknown)$/i;

export function parseOrgAuthClaimToken(hash: string): OrgAuthClaimToken | null {
    const match = /^#token=([0-9a-f-]{36})\.(.+)$/i.exec(hash);
    if (!match) return null;
    return { tokenUuid: match[1], secret: match[2] };
}

export function resolveOrgAuthClaimError(
    status: number,
    body: OrgAuthClaimErrorBody,
    stage: OrgAuthClaimStage
): OrgAuthClaimErrorResolution {
    const code = normalizeCode(body.code);
    const message = body.error?.trim() ?? "";

    if (code === "WEAK_PASSWORD" || /password must be between 8 and 72 characters/i.test(message)) {
        return {
            message: ORG_AUTH_CLAIM_PASSWORD_LENGTH_ERROR,
            scope: "field",
            retryable: false
        };
    }

    if (code && CLAIM_TOKEN_ERROR_MESSAGES[code]) {
        return {
            message: CLAIM_TOKEN_ERROR_MESSAGES[code],
            scope: "page",
            retryable: false
        };
    }

    if (status === 429 || /too many requests|rate limit/i.test(message)) {
        return {
            message: "Too many attempts. Wait a few minutes, then try this link again.",
            scope: stage === "submit" ? "form" : "page",
            retryable: true
        };
    }

    if (status >= 500 || code === "UNKNOWN" || /internal server error/i.test(message)) {
        return {
            message:
                stage === "submit"
                    ? "Password was not set because the server hit an issue. Try again in a moment."
                    : "We couldn't verify this link because the server hit an issue. Try again in a moment.",
            scope: stage === "submit" ? "form" : "page",
            retryable: true
        };
    }

    if (status === 400 || /invalid claim token/i.test(message)) {
        return {
            message: CLAIM_TOKEN_ERROR_MESSAGES.TOKEN_INVALID,
            scope: "page",
            retryable: false
        };
    }

    if (message && !TECHNICAL_CLAIM_MESSAGE.test(message)) {
        return {
            message,
            scope: stage === "submit" ? "form" : "page",
            retryable: false
        };
    }

    return {
        message:
            stage === "submit"
                ? "Password was not set. Check the link and try again."
                : "This link could not be verified. Open the latest invite or password reset link.",
        scope: stage === "submit" ? "form" : "page",
        retryable: false
    };
}

function normalizeCode(code: string | undefined): string | null {
    const normalized = code?.trim();
    return normalized ? normalized.toUpperCase() : null;
}
